import type { EditorState, TransactionSpec } from "@codemirror/state";
import { StateEffect, StateField, Transaction } from "@codemirror/state";
import type { EditorView, ViewUpdate } from "@codemirror/view";
import type { Ref } from "vue";

import { cardActions } from "@/action/cardActions";
import type { Card } from "@/model/card";
import { isDependency } from "@/model/stickyType";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useToastStore } from "@/store/toast";
import { removeNonPrintable } from "@/utils/string";
import { replaceEmoji } from "@/utils/text/emojiReplacer";
import { optimalEditFontSize } from "@/utils/text/fontSizeOptimizer";
import type { Breaks } from "@/utils/text/lineBreaker";
import {
  breaksInStrings,
  deleteNewlineInText,
  hardBreak,
  stringFromLines,
  textFromStrings,
} from "@/utils/text/lineBreaker";
import { hardBreaksAllowed } from "@/utils/text/sticky";

export function useTextBreak(
  card: Card,
  editorRef: Ref<HTMLElement | null>,
  fontSize: Ref<number>,
) {
  const hardBreaks = hardBreaksAllowed(card.type);

  function newline(view: EditorView) {
    if (hardBreaks) {
      view.dispatch(view.state.replaceSelection(hardBreak + "\n"));
    } else {
      useToastStore().show(/*$t*/ "message.noNewlineOnAlmSynced");
    }
    return true;
  }

  const updateBreakState = StateEffect.define<Breaks>();

  // contains the number of soft breaks before the current cursor position
  // we need to know this number so we can adjust the cursor position when a soft break is added/removed
  const breakState = StateField.define<Breaks>({
    create() {
      return [0, 0, 0];
    },
    update(value, tr: Transaction) {
      for (const effect of tr.effects) {
        if (effect.is(updateBreakState)) {
          return effect.value;
        }
      }
      return value;
    },
  });

  function rebreakText(v: ViewUpdate) {
    // update is from composing a dead key (~`^) or rebreaking -> ignore
    if (v.view.composing || v.transactions[0]?.annotation(Transaction.remote) === true) {
      return;
    }
    if (!v.docChanged) {
      // doc not changed -> just update softBreaksBeforeSelection
      v.view.dispatch({
        annotations: Transaction.remote.of(true),
        effects: updateBreakState.of(
          breaksInStrings(v.state.doc.iterLines(), v.state.selection.main.from),
        ),
      });
    } else {
      const selection = { anchor: v.state.selection.main.from };
      // extract the effective text from the editor
      let text = textFromStrings(v.state.doc.iterLines());
      text = handleDeleteNewline(text, selection, v);
      text = replaceChars(text, selection);
      // update the text in vue
      cardActions.setText("action-menu", card.id, text);
      // update the text in the editor with the needed line breaks
      v.view.dispatch(breakText(v.state, selection, text));
    }
  }

  function handleDeleteNewline(text: string, selection: { anchor: number }, v: ViewUpdate) {
    const { text: newText, pos } = deleteNewlineInText(
      text,
      selection.anchor,
      v.state.field(breakState),
      deleteNewlinePos(v),
    );
    selection.anchor = pos;
    return newText;
  }

  // check if the update contains a transaction that deletes a newline
  function deleteNewlinePos(v: ViewUpdate): number | undefined {
    let newlinePos;
    for (const tr of v.transactions) {
      tr.changes.iterChanges((fromA, toA, fromB, _toB, _inserted) => {
        if (toA > fromA && v.startState.doc.sliceString(fromA, fromA + 1) === "\n") {
          newlinePos = fromB;
        }
      });
    }
    return newlinePos;
  }

  function replaceChars(text: string, selection: { anchor: number }) {
    if (!hardBreaks) {
      text = removeNonPrintable(text);
    }
    const replace = replaceEmoji(text);
    if (!replace) {
      return text;
    }
    selection.anchor = replace.updatePos(selection.anchor);
    return replace.text;
  }

  function breakText(
    state: EditorState,
    selection: { anchor: number },
    text: string,
  ): TransactionSpec {
    const font = optimalEditFontSize(editorRef.value!, text, useClientSettingsStore().stickyFont, {
      isDependency: isDependency(card),
      nonStandardCardSize: false,
    });
    fontSize.value = font.size;
    const { s, pos, breaks } = stringFromLines(
      font.lines,
      text,
      selection.anchor,
      state.field(breakState),
    );
    return {
      annotations: Transaction.remote.of(true),
      changes: { from: 0, to: state.doc.length, insert: s },
      selection: { anchor: pos },
      effects: updateBreakState.of(breaks),
    };
  }

  return { breakState, breakText, rebreakText, newline };
}
